import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Minus, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PhotoPicker } from "@/components/PhotoPicker";
import { api, errMsg } from "@/lib/api";

const day = (d) => new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
const TREND = {
  mejor: ["bg-emerald-50 text-emerald-800", "Lo vemos mejor", TrendingUp],
  igual: ["bg-slate-100 text-slate-700", "Lo vemos igual", Minus],
  peor: ["bg-red-50 text-red-800", "Lo vemos peor", TrendingDown],
};

const Photo = ({ fid, n }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/followups/${fid}/photo/${n}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [fid, n]);
  return src ? <img src={src} alt="" className="w-full aspect-[4/3] object-cover rounded" data-testid={`fu-photo-${fid}-${n}`} /> : <div className="w-full aspect-[4/3] bg-slate-100 animate-pulse rounded" />;
};

const Check = ({ c }) => {
  const [cls, label, Icon] = TREND[c.trend];
  return (
    <div className="text-sm space-y-1" data-testid={`fu-check-${c.id}`}>
      <p className={`inline-flex items-center gap-1.5 font-bold rounded-full px-3 py-1 ${cls}`}><Icon size={15} /> {label}</p>
      {c.urgency === "urgente" && <p className="text-[#DC2626] font-bold">Llama a tu veterinario cuanto antes.</p>}
      <p>{c.comparison}</p>
      {c.tip && <p className="text-slate-600"><b>Consejo:</b> {c.tip}</p>}
      {c.photo_request && <p className="bg-[#FEF3C7] text-[#92400E] rounded p-2">{c.photo_request}</p>}
    </div>
  );
};

const NewCheck = ({ fid, reload }) => {
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post(`/followups/${fid}/check`, { photo, note }); toast.success("Comparación lista"); setOpen(false); setPhoto(null); setNote(""); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors" data-testid={`fu-new-${fid}`}>Subir foto de seguimiento</button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B1D3A]">Foto de seguimiento</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Intenta repetir el mismo ángulo y una luz parecida para que la comparación sea fiable.</p>
        <PhotoPicker required photo={photo} setPhoto={setPhoto} note="Guardaremos esta foto en tu seguimiento; solo tú la verás y puedes borrarla." />
        <Textarea rows={2} maxLength={600} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional: qué has notado estos días" data-testid="fu-note" />
        <button disabled={!photo || busy} onClick={run} className="w-full flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-300 text-white font-display font-bold rounded-full py-3" data-testid="fu-submit">
          {busy ? <><Loader2 size={18} className="animate-spin" /> Comparando fotos…</> : "Comparar con la anterior"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

const Card = ({ f, reload }) => (
  <div className="border border-slate-200 rounded p-5" data-testid={`fu-card-${f.id}`}>
    <div className="flex justify-between gap-3">
      <div><p className="font-display font-bold text-xl text-[#0B1D3A]">{f.area_label}</p><p className="text-xs text-slate-500">Iniciado el {day(f.created_at)}</p></div>
      <button onClick={async () => { if (window.confirm("¿Borrar este seguimiento y sus fotos?")) { await api.delete(`/followups/${f.id}`); reload(); } }} className="text-slate-400 hover:text-[#DC2626]" data-testid={`fu-delete-${f.id}`}><Trash2 size={16} /></button>
    </div>
    <div className="grid sm:grid-cols-2 gap-4 mt-4">
      <div><Photo fid={f.id} n={0} /><p className="text-xs text-slate-500 mt-1">{day(f.created_at)} · primera foto</p><p className="text-sm mt-1">{f.observed || f.summary}</p></div>
      {f.checks.map((c, i) => <div key={c.id}><Photo fid={f.id} n={i + 1} /><p className="text-xs text-slate-500 my-1">{day(c.created_at)}</p><Check c={c} /></div>)}
    </div>
    <div className="mt-4">{f.checks.length < 10 && <NewCheck fid={f.id} reload={reload} />}</div>
  </div>
);

export const Followups = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/followups").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  if (!list) return <p className="text-slate-500">Cargando…</p>;
  if (!list.length) return <p className="text-slate-500" data-testid="fu-empty">Todavía no tienes seguimientos. Haz un <Link to="/diagnostico" className="text-[#DC2626] font-bold">Diagnóstico FAUNNY</Link> con foto y aquí podrás comparar su evolución.</p>;
  return <div className="space-y-6" data-testid="followups">{list.map((f) => <Card key={f.id} f={f} reload={load} />)}</div>;
};
